import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button, Modal, Form, Alert, Badge } from "react-bootstrap";
import DynamicTable from "../../components/DynamicTable";
import { fetchClosedDays, addClosedDay, deleteClosedDay } from "./DataService";

function JoursFeries() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({ date: "", label: "", is_recurring: false });

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchClosedDays();
      setDays(data);
    } catch (e) {
      setError("Impossible de charger les jours fermés.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce jour ?")) return;
    try {
      await deleteClosedDay(id);
      setDays((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      alert("Erreur lors de la suppression.");
    }
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!form.date || !form.label) {
      setFormError("La date et le libellé sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const created = await addClosedDay(form);
      setDays((prev) => [...prev, created].sort((a, b) => a.date.localeCompare(b.date)));
      setShowModal(false);
      setForm({ date: "", label: "", is_recurring: false });
    } catch (e) {
      const msg = e?.response?.data?.errors?.date?.[0] ?? "Erreur lors de l'ajout.";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Format react-table v7 (Header / accessor / Cell)
  const columns = useMemo(() => [
    {
      Header: "Date",
      accessor: "date",
      Cell: ({ value }) =>
        new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }),
    },
    {
      Header: "Libellé",
      accessor: "label",
    },
    {
      Header: "Type",
      accessor: "is_recurring",
      Cell: ({ value }) =>
        value ? (
          <Badge bg="primary">Récurrent</Badge>
        ) : (
          <Badge bg="warning" text="dark">Exceptionnel</Badge>
        ),
    },
    {
      Header: "Action",
      accessor: "id",
      Cell: ({ value }) => (
        <Button variant="danger" size="sm" onClick={() => handleDelete(value)}>
          Supprimer
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [days]);

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Jours fériés &amp; fermetures</h4>
          <small className="text-muted">
            Les fériés récurrents se répètent chaque année (mois + jour). Les fermetures exceptionnelles
            sont vérifiées à la date exacte.
          </small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowModal(true)}>
            + Ajouter
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <p className="text-muted">Chargement…</p>}

      {!loading && (
        <DynamicTable
          columns={columns}
          data={days}
          total={days.length}
          last_page={1}
          nbr={1}
          setNbr={() => {}}
        />
      )}

      <Modal show={showModal} onHide={() => { setShowModal(false); setFormError(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un jour fermé</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Libellé</Form.Label>
              <Form.Control
                type="text"
                placeholder="ex: Fermeture estivale"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Récurrent chaque année (férié fixe)"
                checked={form.is_recurring}
                onChange={(e) => setForm((f) => ({ ...f, is_recurring: e.target.checked }))}
              />
              <Form.Text className="text-muted">
                Si coché, la vérification se fait sur le mois et le jour uniquement.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setFormError(null); }}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default JoursFeries;
